/*
 * Elexa <elexa-before-after>: drag-to-reveal comparison slider (snippets/elexa-before-after.liquid).
 * P = position in %, clamped 0-100, mirrored into --ex-cmp-pos and the handle's aria-valuenow.
 * Mouse: drag from the handle or anywhere on the box; a plain click on the box jumps there.
 * Touch: `touch-action: pan-y` lets vertical swipes scroll the page (the browser cancels the
 *        pointer); horizontal drags past 5px move the divider; a tap under 200ms jumps.
 * Keyboard on the handle: Left/Right -5/+5, Home 0, End 100.
 * Each element is independent, so two copies on one page work. No dependencies.
 */
(() => {
  if (customElements.get('elexa-before-after')) return;

  const STEP = 5;
  const MOVE_THRESHOLD = 5;
  const TAP_MS = 200;
  const clamp = (n) => Math.min(100, Math.max(0, n));

  class ElexaBeforeAfter extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.box = this.querySelector('[data-ex-cmp-box]');
      this.handle = this.querySelector('[data-ex-cmp-handle]');
      if (!this.box || !this.handle) return;
      this.initialised = true;
      this.position = clamp(parseFloat(this.dataset.position || '50'));
      this.drag = null;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.box.addEventListener('pointerdown', this.onPointerDown);
      this.box.addEventListener('pointermove', this.onPointerMove);
      this.box.addEventListener('pointerup', this.onPointerUp);
      this.box.addEventListener('pointercancel', this.onPointerCancel);
      this.box.addEventListener('lostpointercapture', this.onPointerCancel);
      this.box.addEventListener('dragstart', (e) => e.preventDefault());
      this.handle.addEventListener('keydown', this.onKeyDown);
    }

    disconnectedCallback() {
      if (!this.initialised) return;
      this.initialised = false;
      this.box.removeEventListener('pointerdown', this.onPointerDown);
      this.box.removeEventListener('pointermove', this.onPointerMove);
      this.box.removeEventListener('pointerup', this.onPointerUp);
      this.box.removeEventListener('pointercancel', this.onPointerCancel);
      this.box.removeEventListener('lostpointercapture', this.onPointerCancel);
      this.handle.removeEventListener('keydown', this.onKeyDown);
      cancelAnimationFrame(this.raf);
    }

    /** Position in % for a client X coordinate. */
    percentFromX(clientX) {
      const rect = this.box.getBoundingClientRect();
      if (!rect.width) return this.position;
      return clamp(((clientX - rect.left) / rect.width) * 100);
    }

    /** Set P (0-100); rendering is batched to one write per animation frame. */
    setPosition(value) {
      const next = Math.round(clamp(value) * 10) / 10;
      if (next === this.position && this.rendered) return;
      this.position = next;
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.rendered = true;
        this.style.setProperty('--ex-cmp-pos', `${this.position}%`);
        const rounded = Math.round(this.position);
        this.handle.setAttribute('aria-valuenow', String(rounded));
        this.handle.setAttribute('aria-valuetext', `${rounded}%`);
        this.dispatchEvent(new CustomEvent('elexa:before-after-change', { bubbles: true, detail: { position: this.position } }));
      });
    }

    onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      const onHandle = Boolean(e.target.closest('[data-ex-cmp-handle]'));
      this.drag = {
        id: e.pointerId,
        type: e.pointerType || 'mouse',
        startX: e.clientX,
        startY: e.clientY,
        time: Date.now(),
        onHandle,
        moved: false,
      };
      if (this.drag.type === 'mouse' || this.drag.type === 'pen') {
        // Mouse: no text or image selection, follow the pointer anywhere on the page.
        e.preventDefault();
        if (onHandle) this.handle.focus({ preventScroll: true });
        this.capture(e.pointerId);
        this.classList.add('is-dragging');
      }
    }

    capture(pointerId) {
      try {
        this.box.setPointerCapture(pointerId);
      } catch (err) {
        /* pointer already released */
      }
    }

    onPointerMove(e) {
      const d = this.drag;
      if (!d || d.id !== e.pointerId) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      if (!d.moved) {
        if (Math.abs(dx) < MOVE_THRESHOLD && Math.abs(dy) < MOVE_THRESHOLD) return;
        if (d.type === 'touch' && Math.abs(dy) > Math.abs(dx)) {
          // Vertical intent: leave the page scroll to the browser.
          this.drag = null;
          return;
        }
        d.moved = true;
        if (d.type === 'touch') {
          this.capture(e.pointerId);
          this.classList.add('is-dragging');
        }
      }
      if (e.cancelable) e.preventDefault();
      this.setPosition(this.percentFromX(e.clientX));
    }

    onPointerUp(e) {
      const d = this.drag;
      if (!d || d.id !== e.pointerId) return;
      this.drag = null;
      this.classList.remove('is-dragging');
      if (!d.moved && !d.onHandle) {
        const quick = d.type !== 'touch' || Date.now() - d.time < TAP_MS;
        if (quick) this.setPosition(this.percentFromX(e.clientX));
      }
    }

    onPointerCancel(e) {
      // lostpointercapture bubbles: a child losing its implicit touch capture to the box is not a cancel.
      if (e.type === 'lostpointercapture' && e.target !== this.box) return;
      if (this.drag && e.pointerId !== undefined && this.drag.id !== e.pointerId) return;
      this.drag = null;
      this.classList.remove('is-dragging');
    }

    onKeyDown(e) {
      let next = null;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = this.position - STEP;
          break;
        case 'ArrowRight':
        case 'ArrowUp':
          next = this.position + STEP;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = 100;
          break;
        default:
          return;
      }
      e.preventDefault();
      this.setPosition(Math.round(next));
    }
  }

  customElements.define('elexa-before-after', ElexaBeforeAfter);
})();
